import { Maximize2, Minimize2, ZoomIn, ZoomOut } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";

interface ImagePreviewProps {
  src: string;
  alt: string;
}

const ZOOM_LEVELS = [25, 50, 75, 100, 125, 150, 200, 300];
const DEFAULT_ZOOM_INDEX = 3; // 100%

const ImagePreview: React.FC<ImagePreviewProps> = ({ src, alt }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
  const [isFitMode, setIsFitMode] = useState(true);

  const zoomLevel = ZOOM_LEVELS[zoomIndex];

  const handleZoomIn = useCallback(() => {
    setIsFitMode(false);
    setZoomIndex((prev) => Math.min(prev + 1, ZOOM_LEVELS.length - 1));
  }, []);

  const handleZoomOut = useCallback(() => {
    setIsFitMode(false);
    setZoomIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleFit = useCallback(() => {
    setIsFitMode(true);
    setZoomIndex(DEFAULT_ZOOM_INDEX);
  }, []);

  const handleActualSize = useCallback(() => {
    setIsFitMode(false);
    setZoomIndex(DEFAULT_ZOOM_INDEX);
  }, []);

  // Keyboard zoom controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "+" || e.key === "=") {
        handleZoomIn();
      } else if (e.key === "-") {
        handleZoomOut();
      } else if (e.key === "0") {
        handleFit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleZoomIn, handleZoomOut, handleFit]);

  return (
    <div className="flex flex-col h-full">
      {/* Image container */}
      <div className="flex-1 overflow-auto flex items-center justify-center p-4">
        <img
          src={src}
          alt={alt}
          className={`max-w-none transition-transform ${
            isFitMode ? "max-h-full max-w-full object-contain" : ""
          }`}
          style={
            !isFitMode
              ? {
                  width: `${zoomLevel}%`,
                  height: "auto",
                }
              : undefined
          }
          draggable={false}
        />
      </div>

      {/* Zoom controls */}
      <div
        className={`flex items-center justify-center space-x-2 py-3 border-t ${
          isDarkMode ? "border-rp-separator bg-rp-surface/50" : "border-gray-200 bg-gray-50"
        }`}
      >
        <button
          onClick={handleZoomOut}
          disabled={zoomIndex === 0 && !isFitMode}
          className={`p-2 rounded-lg transition-colors ${
            isDarkMode
              ? "hover:bg-rp-surface-raised text-rp-grey/70 disabled:text-rp-grey/25"
              : "hover:bg-gray-200 text-gray-700 disabled:text-gray-400"
          }`}
          title="Zoom Out (-)"
        >
          <ZoomOut className="w-5 h-5" />
        </button>

        <span
          className={`min-w-[60px] text-center text-sm font-medium ${
            isDarkMode ? "text-rp-grey/70" : "text-gray-700"
          }`}
        >
          {isFitMode ? "Fit" : `${zoomLevel}%`}
        </span>

        <button
          onClick={handleZoomIn}
          disabled={zoomIndex === ZOOM_LEVELS.length - 1}
          className={`p-2 rounded-lg transition-colors ${
            isDarkMode
              ? "hover:bg-rp-surface-raised text-rp-grey/70 disabled:text-rp-grey/25"
              : "hover:bg-gray-200 text-gray-700 disabled:text-gray-400"
          }`}
          title="Zoom In (+)"
        >
          <ZoomIn className="w-5 h-5" />
        </button>

        <div
          className={`w-px h-6 mx-2 ${isDarkMode ? "bg-rp-surface-raised" : "bg-gray-300"}`}
        />

        <button
          onClick={handleFit}
          className={`p-2 rounded-lg transition-colors ${
            isFitMode
              ? isDarkMode
                ? "bg-teal-900/50 text-teal-400"
                : "bg-teal-100 text-teal-700"
              : isDarkMode
                ? "hover:bg-rp-surface-raised text-rp-grey/70"
                : "hover:bg-gray-200 text-gray-700"
          }`}
          title="Fit to Screen (0)"
        >
          <Minimize2 className="w-5 h-5" />
        </button>

        <button
          onClick={handleActualSize}
          className={`p-2 rounded-lg transition-colors ${
            !isFitMode && zoomLevel === 100
              ? isDarkMode
                ? "bg-teal-900/50 text-teal-400"
                : "bg-teal-100 text-teal-700"
              : isDarkMode
                ? "hover:bg-rp-surface-raised text-rp-grey/70"
                : "hover:bg-gray-200 text-gray-700"
          }`}
          title="Actual Size"
        >
          <Maximize2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default ImagePreview;

